import type { ApiSchema } from "../../../api/apiSchema";
import {
  createCorpus,
  importJsonLines,
  type CorpusImportResult,
  type CorpusSummary,
  type ImportMode,
  type SegmentationConfigurationInput,
} from "../../../api/content/corporaApi";
import { isJsonObject } from "../../../api/http/jsonDecoding";
import { readableJsonText } from "../../../shared/formatting/readableJson";

/**
 * What one import does: create the corpus if asked, then import the JSON Lines file, with a
 * new segmentation if one is given, as one new corpus version. The import names the version
 * it builds on, so a concurrent change fails with `content_version_conflict` instead of
 * being overwritten.
 */
export type ImportTarget =
  | { readonly kind: "newCorpus"; readonly projectId: string; readonly name: string; readonly segmentation: SegmentationConfigurationInput }
  | { readonly kind: "existingCorpus"; readonly corpus: CorpusSummary; readonly newSegmentation: SegmentationConfigurationInput | null };

export interface CorpusImportOutcome {
  readonly corpusId: string;
  readonly result: CorpusImportResult;
}

/** The import's corpus, created first if it is new. A corpus created before a failed import exists all the same. */
export async function openTargetCorpus(target: ImportTarget): Promise<CorpusSummary> {
  return target.kind === "newCorpus" ? await createCorpus(target.projectId, target.name, target.segmentation) : target.corpus;
}

export async function runCorpusImport(target: ImportTarget, file: Blob, mode: ImportMode): Promise<CorpusImportOutcome> {
  const corpus = await openTargetCorpus(target);
  const newSegmentation = target.kind === "existingCorpus" ? target.newSegmentation : null;
  const result = await importJsonLines(corpus.corpusId, file, mode, corpus.latestVersion, newSegmentation);
  return { corpusId: corpus.corpusId, result };
}

/** The segmentation as editable text, in the readable layout. */
export function segmentationText(segmentation: SegmentationConfigurationInput): string {
  return readableJsonText(segmentation);
}

/** Read the segmentation text the user typed; `null` if it is not a JSON object. */
export function parseSegmentationText(text: string): SegmentationConfigurationInput | null {
  try {
    const parsed: unknown = JSON.parse(text);
    return isJsonObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Starting points for the segmentation (`docs/components/content.md` D-6). */
export const SEGMENTATION_TEMPLATES = {
  /** The converter gives each document its segment tree, as DMW's text converter does. */
  fromInput: { format: "dmw-segmentation/1", document_segment_type: "document", rules: {}, source: "input", key_forms: [] },
  /** Each document is one segment. */
  flat: { format: "dmw-segmentation/1", document_segment_type: "document", rules: {}, source: "rules", key_forms: [] },
  /** A head, then parts that each start with a number such as `1. ` at a line start. */
  headAndNumberedParts: {
    format: "dmw-segmentation/1",
    document_segment_type: "entry",
    rules: {
      entry: {
        boundary_pattern: "(?m)^\\d+\\.\\s",
        leading_segment_type: "head",
        repeated_segment_type: "subentry",
        trim_whitespace: true,
      },
    },
    source: "rules",
    key_forms: [],
  },
} as const satisfies Record<string, ApiSchema<"SegmentationConfiguration">>;

export type SegmentationTemplateName = keyof typeof SEGMENTATION_TEMPLATES;

export function segmentationTemplateText(name: SegmentationTemplateName): string {
  return segmentationText(SEGMENTATION_TEMPLATES[name]);
}

/** Whether the text is still one of the templates, so a new choice of what is imported may replace it. */
export function isTemplateText(text: string): boolean {
  return (Object.keys(SEGMENTATION_TEMPLATES) as SegmentationTemplateName[]).some((name) => segmentationTemplateText(name) === text);
}

/** The corpus choice's value for a new corpus. */
export const NEW_CORPUS = "";

/** What the person chose as the import's corpus, before it is checked. */
export interface CorpusTargetDraft {
  /** `""`: a new corpus. */
  readonly corpusId: string;
  readonly newName: string;
  readonly segmentationText: string;
  /** Only for a JSON Lines import into an existing corpus. */
  readonly replaceSegmentation: boolean;
}

export function corpusTargetDraft(corpusId: string | null, template: SegmentationTemplateName): CorpusTargetDraft {
  return { corpusId: corpusId ?? NEW_CORPUS, newName: "", segmentationText: segmentationTemplateText(template), replaceSegmentation: false };
}

/** The import target, `"invalidSegmentation"` if the segmentation is no JSON object, `null` while incomplete. */
export function importTargetOf(draft: CorpusTargetDraft, corpora: readonly CorpusSummary[], projectId: string): ImportTarget | "invalidSegmentation" | null {
  const corpus = corpora.find((candidate) => candidate.corpusId === draft.corpusId) ?? null;
  if (corpus !== null && !draft.replaceSegmentation) return { kind: "existingCorpus", corpus, newSegmentation: null };
  const segmentation = parseSegmentationText(draft.segmentationText);
  if (segmentation === null) return "invalidSegmentation";
  if (corpus !== null) return { kind: "existingCorpus", corpus, newSegmentation: segmentation };
  return draft.newName.trim() === "" ? null : { kind: "newCorpus", projectId, name: draft.newName.trim(), segmentation };
}


/** One `.jsonl` file goes to the direct import (D-7); anything else is uploaded and converted in a Thread (D-18). */
export function isJsonLinesFile(files: readonly { readonly path: string }[]): boolean {
  const only = files.length === 1 ? files[0] : undefined;
  return only !== undefined && /\.(jsonl|ndjson)$/i.test(only.path);
}
